import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function CheckoutPage() {
  const { items, subtotal, shipping, total, count, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    line1: "",
    line2: "",
    suburb: "",
    state: "SA",
    postcode: "",
    notes: "",
    payment_method: "stripe",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (count === 0) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        name: form.name,
        email: form.email,
        phone: form.phone,
        shipping: {
          line1: form.line1,
          line2: form.line2,
          suburb: form.suburb,
          state: form.state,
          postcode: form.postcode,
        },
        payment_method: form.payment_method,
        origin_url: window.location.origin,
        notes: form.notes,
      };
      const r = await api.post("/shop/checkout", payload);
      // Persist a lightweight marker so /order/confirmed can offer a "back to shop" experience
      sessionStorage.setItem("seven7h_last_order_id", r.data.id);
      // Clear cart client-side (server has snapshot)
      clear();
      window.location.href = r.data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  if (count === 0) {
    return (
      <div className="grain bg-[#050505] min-h-screen">
        <Nav />
        <main className="pt-32 md:pt-40 pb-24">
          <div className="max-w-[900px] mx-auto px-6 md:px-12 py-24 text-center">
            <p className="eyebrow mb-4">Checkout</p>
            <h1 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] mb-6">
              Your cart is <span className="italic text-brand-red">empty.</span>
            </h1>
            <Link to="/shop" className="btn-primary inline-flex mt-8">
              Browse the Shop <ArrowRight size={14} />
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="grain bg-[#050505] min-h-screen">
      <Nav />
      <main className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <p className="md:col-span-5 eyebrow">Checkout — Secure Payment</p>
            <h1 className="md:col-span-7 font-serif text-5xl md:text-7xl text-white leading-[0.95]">
              Complete your <span className="italic text-brand-red">order.</span>
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <form
              onSubmit={submit}
              className="lg:col-span-7 space-y-12"
              data-testid="checkout-form"
            >
              <section>
                <p className="eyebrow mb-6">01 · Contact</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Full Name</label>
                    <input
                      required
                      data-testid="checkout-name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Email</label>
                    <input
                      required
                      type="email"
                      data-testid="checkout-email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Phone</label>
                    <input
                      required
                      data-testid="checkout-phone"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                </div>
              </section>

              <section>
                <p className="eyebrow mb-6">02 · Shipping Address</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Address Line 1</label>
                    <input
                      required
                      data-testid="checkout-line1"
                      value={form.line1}
                      onChange={(e) => set("line1", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="eyebrow block mb-3">Address Line 2 (optional)</label>
                    <input
                      data-testid="checkout-line2"
                      value={form.line2}
                      onChange={(e) => set("line2", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Suburb</label>
                    <input
                      required
                      data-testid="checkout-suburb"
                      value={form.suburb}
                      onChange={(e) => set("suburb", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">State</label>
                    <input
                      required
                      data-testid="checkout-state"
                      value={form.state}
                      onChange={(e) => set("state", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Postcode</label>
                    <input
                      required
                      data-testid="checkout-postcode"
                      value={form.postcode}
                      onChange={(e) => set("postcode", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-3">Order Notes (optional)</label>
                    <input
                      data-testid="checkout-notes"
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      className="input-minimal"
                    />
                  </div>
                </div>
              </section>

              <section>
                <p className="eyebrow mb-6">03 · Payment Method</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="checkout-pay-stripe"
                    onClick={() => set("payment_method", "stripe")}
                    className={`py-4 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                      form.payment_method === "stripe"
                        ? "border-white bg-white text-black"
                        : "border-white/10 hover:border-white/30 text-neutral-300"
                    }`}
                  >
                    Card · Stripe
                  </button>
                  <button
                    type="button"
                    data-testid="checkout-pay-paypal"
                    onClick={() => set("payment_method", "paypal")}
                    className={`py-4 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                      form.payment_method === "paypal"
                        ? "border-white bg-white text-black"
                        : "border-white/10 hover:border-white/30 text-neutral-300"
                    }`}
                  >
                    PayPal
                  </button>
                </div>
              </section>

              {error && (
                <p className="text-red-400 text-sm font-mono" data-testid="checkout-error">
                  {error}
                </p>
              )}

              <div className="pt-6 border-t border-white/10">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-submit"
                  className="btn-primary w-full md:w-auto justify-center"
                >
                  {submitting
                    ? "Redirecting…"
                    : form.payment_method === "paypal"
                      ? `Pay $${total.toFixed(2)} with PayPal`
                      : `Pay $${total.toFixed(2)} · Card`}
                  <ArrowRight size={14} />
                </button>
              </div>
            </form>

            {/* Summary */}
            <aside className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <div className="border border-white/10 p-6 md:p-8 bg-white/[0.02]">
                <p className="eyebrow mb-6">Order Summary · {count} items</p>
                <ul className="divide-y divide-white/10">
                  {items.map((it) => (
                    <li key={it.slug} className="py-4 flex gap-3">
                      <div className="w-14 h-14 shrink-0 bg-[#0a0a0a] overflow-hidden">
                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white leading-snug">{it.name}</p>
                        <p className="mt-1 text-neutral-500 text-xs">
                          Qty {it.quantity} · ${it.price.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-white font-serif">${(it.price * it.quantity).toFixed(2)}</div>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex justify-between text-neutral-400">
                    <span>Subtotal</span>
                    <span className="text-white">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-400">
                    <span>Shipping</span>
                    <span className="text-white">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between items-baseline pt-4 border-t border-white/10">
                    <span className="eyebrow">Total AUD</span>
                    <span className="font-serif text-3xl text-white" data-testid="checkout-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
